import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { VerifiedBadge } from "@/components/VerifiedBadge";

const LOCALES = { fr: "fr-FR", en: "en-GB", es: "es-ES", pt: "pt-PT" };

// « Demain » / « jeu. 25 sept. » — libellé du groupe d'un anniversaire à venir.
export const upcomingDayLabel = (isoDate, inDays, lang, t) => {
  if (inDays === 1) return t("birthdays.tomorrow", "Demain");
  const d = new Date(`${isoDate}T12:00:00`);
  if (isNaN(d)) return isoDate;
  const s = d.toLocaleDateString(LOCALES[lang] || "fr-FR", { weekday: "short", day: "numeric", month: "short" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Regroupe les anniversaires par jour (ordre serveur conservé : date, puis abonnements, puis nom).
export const groupUpcoming = (items) => {
  const out = [];
  for (const it of items || []) {
    const last = out[out.length - 1];
    if (last && last.in_days === it.in_days) last.items.push(it);
    else out.push({ in_days: it.in_days, date: it.date, items: [it] });
  }
  return out;
};

// Liste dépliée « Anniversaires à venir » (façon Facebook) : chargée à l'ouverture, tap → profil.
export const UpcomingBirthdays = ({ days }) => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, items: [] });

  useEffect(() => {
    let alive = true;
    api.get("/users/birthdays/upcoming")
      .then(({ data }) => { if (alive) setState({ loading: false, items: data.items || [] }); })
      .catch(() => { if (alive) setState({ loading: false, items: [] }); });
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <div className="mt-3 space-y-2 animate-pulse" data-testid="birthdays-upcoming-loading">
        {[0, 1].map((i) => <div key={i} style={{ height: 36, borderRadius: 10, background: "rgba(128,128,128,0.12)" }} />)}
      </div>
    );
  }
  if (state.items.length === 0) {
    return (
      <p className="mt-3 text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="birthdays-upcoming-empty">
        {t("birthdays.upcoming_empty", "Aucun anniversaire dans les {days} prochains jours.").replace("{days}", days)}
      </p>
    );
  }
  return (
    <div className="mt-3 space-y-3" data-testid="birthdays-upcoming-list">
      {groupUpcoming(state.items).map((g) => (
        <div key={g.in_days} data-testid={`birthdays-upcoming-day-${g.in_days}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={`birthdays-upcoming-day-label-${g.in_days}`}>
            {upcomingDayLabel(g.date, g.in_days, lang, t)}
          </div>
          <ul className="space-y-1">
            {g.items.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => nav(`/profile/${p.id}`)}
                  className="w-full flex items-center gap-3 text-left rounded-xl px-2 py-1.5 -mx-2 transition-colors hover:bg-[rgba(0,0,0,0.04)]"
                  data-testid={`birthdays-upcoming-${p.id}`}
                  data-followed={p.followed ? "1" : "0"}
                >
                  <Avatar className="h-8 w-8"><AvatarImage src={avatarSrc(p)} /><AvatarFallback>{p.name?.[0]}</AvatarFallback></Avatar>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1 text-sm font-medium truncate">
                      <span className="truncate">{p.name}</span>
                      {p.is_verified && <VerifiedBadge user={p} size={12} interactive={false} />}
                    </span>
                    {p.followed && <span className="text-[10px] font-semibold" style={{ color: "var(--yalola-primary)" }}>{t("birthdays.followed", "Abonné(e)")}</span>}
                  </span>
                  {p.age_next != null && (
                    <span className="text-xs shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{t("birthdays.will_turn", "aura {age} ans").replace("{age}", p.age_next)}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};
